/**
 * fontainefeedPipeline — water falling UP into a hanging delta.
 *
 * An inverted fountain: a wide, calm "sky-river" delta hangs above
 * the scene. From it, dozens of water droplets arc DOWN and then
 * back UP into a small basin at the bottom. The visual is an
 * "upward fountain" or "ceiling estuary" — water flows from the
 * hanging delta down to the basin, then is shot back up.
 *
 * Innovations:
 *   - Ballistic trajectories: each droplet follows a true parabolic
 *     arc with gravity constant, so the stream shape is authentic.
 *   - Phase stagger: each droplet has its own phase offset, so the
 *     streams form a continuous "ribbon" rather than discrete balls.
 *   - Surface ripple at the basin: when droplets re-enter the basin,
 *     they create expanding ripples using a radial wave.
 *   - Mirror river at top: the delta reflects subtly so the
 *     "hanging river" feels massive.
 *   - Mid drives the recapture rate (high mid = spray),
 *     high drives the speckle/sparkle on droplet tips.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface FontainefeedPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords (origin at center).
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — sky gradient.
  vec3 bg = mix(
    vec3(0.18, 0.32, 0.55),   // upper: pale sky
    vec3(0.06, 0.10, 0.20),   // lower: deep ambient
    smoothstep(0.0, 0.6, -p.y)
  );

  vec3 col = bg;

  // --- The hanging delta (sky-river) ---
  // A wide, flat ellipse centered at y = 0.55, full width.
  float deltaY = 0.55;
  float deltaR = 0.45;
  float dy = p.y - deltaY;
  float dx = p.x;
  float deltaD = sqrt(dx*dx/(deltaR*deltaR) + dy*dy/(0.06*0.06)) - 1.0;
  // The delta's surface — a slightly disturbed ellipse.
  float deltaSurface = smoothstep(0.0, -0.02, deltaD);
  // Surface ripples on the delta.
  float ripple = sin(dx * 20.0 + uTime * 3.0) * 0.003 +
                 sin(dx * 35.0 - uTime * 4.5) * 0.002;
  float waterTop = deltaY + 0.06 + ripple;
  float surface = smoothstep(waterTop + 0.005, waterTop - 0.005, p.y);
  // The water mass: where surface > 0, we're inside the delta.
  float waterIn = surface * smoothstep(0.0, -0.05, deltaD);
  vec3 waterColor = vec3(0.50, 0.78, 0.95);
  col = mix(col, waterColor, waterIn * 0.85);
  // Edge highlight (the water surface from below).
  float edge = smoothstep(0.005, 0.0, abs(p.y - waterTop)) * deltaSurface;
  col += vec3(0.8, 0.95, 1.0) * edge * 0.6;

  // --- The basin at the bottom ---
  float basinY = -0.55;
  float basinR = 0.18;
  float by = p.y - basinY;
  float basinD = sqrt(dx*dx/(basinR*basinR) + by*by/(0.04*0.04)) - 1.0;
  float basinWater = smoothstep(0.0, -0.01, basinD) * smoothstep(0.06, 0.0, p.y - basinY);
  col = mix(col, vec3(0.40, 0.70, 0.92), basinWater * 0.9);
  // Basin rim.
  float rim = smoothstep(0.005, 0.0, abs(basinD)) * smoothstep(0.10, 0.0, abs(basinD));
  col += vec3(0.7, 0.85, 1.0) * rim * 0.3;

  // --- Falling droplets (going DOWN from the delta) ---
  // Each droplet starts at a random x along the delta's surface
  // and arcs down to the basin. It takes T seconds to fall.
  float fallT = 1.4;
  float launchRange = 0.32;
  float g = 1.0;  // gravity
  float vMag = 0.4 + 0.4 * mid;
  const int N = 32;
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    // X-launch position distributed across the delta.
    float lx = (hash21(vec2(fi, 17.0)) - 0.5) * launchRange * 2.0;
    // Phase offset — when the droplet was launched (0..fallT).
    float phase = fract(uTime / fallT + hash21(vec2(fi, 23.0)) * 0.65);
    // Drop time from 0 to fallT.
    float t = phase * fallT;
    // Initial velocity: (down, with some left/right variance).
    float vy = -vMag - 0.2 * hash21(vec2(fi, 31.0));
    float vx = 0.15 * sin(hash21(vec2(fi, 41.0)) * 6.2831);
    // Position at time t.
    float x = lx + vx * t;
    float y = waterTop + vy * t - 0.5 * g * t * t;
    // Skip droplets that have already hit the basin.
    if (y < basinY + 0.04) continue;
    // Distance to the droplet.
    float d = length(p - vec2(x, y));
    // Render as a small bright droplet with a soft trail.
    float trail = exp(-d * 50.0);
    float core = exp(-d * 200.0);
    vec3 dropColor = vec3(0.75, 0.92, 1.0);
    col += dropColor * trail * 0.5;
    col += vec3(1.0, 1.0, 1.0) * core * 0.8;
  }

  // --- Rising droplets (shot UP from the basin) ---
  // These droplets arc from the basin back up into the delta.
  float riseT = 1.6;
  const int M = 24;
  for (int i = 0; i < M; i++) {
    float fi = float(i);
    float lx = (hash21(vec2(fi, 53.0)) - 0.5) * 0.5;
    float phase = fract(uTime / riseT + hash21(vec2(fi, 59.0)) * 0.7);
    float t = phase * riseT;
    float vMagR = 0.7 + 0.6 * mid;
    float vy = vMagR + 0.3 * hash21(vec2(fi, 67.0));
    float vx = 0.20 * sin(hash21(vec2(fi, 71.0)) * 6.2831);
    float x = lx + vx * t;
    float y = basinY + 0.05 + vy * t - 0.5 * g * t * t;
    if (y > waterTop) continue;
    float d = length(p - vec2(x, y));
    float trail = exp(-d * 45.0);
    float core = exp(-d * 180.0);
    vec3 upColor = vec3(0.85, 0.95, 1.0);
    col += upColor * trail * 0.5;
    col += vec3(1.0) * core * 0.7;
  }

  // --- Ripples at the basin where droplets land ---
  // Each impact ring is a radial wave that expands from the impact
  // point and fades over time.
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float phase = fract(uTime * 0.4 + hash21(vec2(fi, 79.0)) * 0.8);
    float rippleR = phase * 0.15;
    vec2 impact = vec2(
      (hash21(vec2(fi, 83.0)) - 0.5) * 0.4,
      basinY + 0.05
    );
    float rd = abs(length(p - impact) - rippleR);
    float ring = smoothstep(0.008, 0.0, rd) * (1.0 - phase);
    col += vec3(0.85, 0.95, 1.0) * ring * 0.6;
  }

  // --- Splashes at the upper delta where droplets merge back ---
  // Where the rising droplets hit the delta surface, draw a small
  // splash — a downward-pointing cone of mist.
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    float phase = fract(uTime * 0.5 + hash21(vec2(fi, 89.0)) * 0.6);
    float hx = (hash21(vec2(fi, 97.0)) - 0.5) * 0.4;
    float hy = waterTop - phase * 0.05;
    float hd = length(p - vec2(hx, hy));
    float splash = exp(-hd * 60.0) * (1.0 - phase);
    col += vec3(0.9, 0.97, 1.0) * splash * 0.4;
  }

  // --- Mist/haze along the entire flow ---
  float mist = valueNoise(p * 3.0 + vec2(uTime * 0.3, 0.0));
  mist = smoothstep(0.4, 0.7, mist) * 0.06;
  col += vec3(0.7, 0.85, 1.0) * mist;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Soft vignette.
  col *= 1.0 - smoothstep(0.7, 1.1, length(p)) * 0.4;

  outColor = vec4(col, 1.0);
}

`;

export function computeFontainefeedBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeFontainefeedRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createFontainefeedPipeline(gl: WebGL2RenderingContext): FontainefeedPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawFontainefeed(
  gl: WebGL2RenderingContext,
  pipe: FontainefeedPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
